import { createTransform } from 'redux-persist';
import { VisitsState } from '../../lib/types';
import { EvidenceState } from '../slices/evidenceSlice';
import { CustomersState } from '../slices/customersSlice';

/**
 * Transform for visits slice - exclude sensitive data and large objects
 */
export const visitsTransform = createTransform(
  // Inbound: Clean data before persisting
  (inboundState: VisitsState) => {
    return {
      ...inboundState,
      // Clear any large cached data to prevent storage bloat
      visits: (inboundState.visits || []).map(visit => ({
        ...visit,
        // Remove any potential large objects from visits
        evidence: [], // Evidence will be persisted separately
      })),
      // Keep active visit for recovery but sanitize it
      activeVisit: inboundState.activeVisit ? {
        ...inboundState.activeVisit,
        evidence: [], // Remove evidence blobs
      } : null,
      // Keep essential metadata for recovery
      isLoading: false, // Reset loading states
      error: null, // Clear errors on persist
    };
  },
  // Outbound: Restore data when loading from storage
  (outboundState: VisitsState) => {
    return {
      ...outboundState,
      // Initialize with clean loading states
      isLoading: false,
      error: null,
      // Keep other state as-is for recovery
    };
  },
  {
    // Only apply to visits slice
    whitelist: ['visits']
  }
);

/**
 * Transform for evidence slice - persist media with smart size management
 * Keeps small files (thumbnails) and metadata for larger files
 */
export const evidenceTransform = createTransform(
  // Inbound: Smart persistence - keep small files, metadata for large files
  (inboundState: EvidenceState) => {
    const MAX_DATA_URL_SIZE = 500000; // 500KB threshold for inline storage

    return {
      ...inboundState,
      // Keep evidence with smart data URL handling - ensure it's an array
      evidence: (Array.isArray(inboundState.evidence) ? inboundState.evidence : []).map(item => {
        const dataUrlSize = item.dataUrl ? item.dataUrl.length : 0;
        const shouldKeepDataUrl = dataUrlSize > 0 && dataUrlSize <= MAX_DATA_URL_SIZE;

        return {
          ...item,
          blob: undefined, // Always remove blob data
          // Keep data URL for small files (thumbnails, small images)
          dataUrl: shouldKeepDataUrl ? item.dataUrl : undefined,
          // Store metadata for restoration
          _persistenceMetadata: {
            hadDataUrl: !!item.dataUrl,
            originalSize: dataUrlSize,
            wasLarge: dataUrlSize > MAX_DATA_URL_SIZE,
            timestamp: Date.now(),
          },
        };
      }),
      // Clear current recording state (shouldn't persist active recordings)
      isRecording: false,
      recordingStartTime: null,
      // Keep essential state for recovery
      uploadProgress: {}, // Reset upload progress
      uploadErrors: [], // Clear upload errors
    };
  },
  // Outbound: Restore evidence state
  (outboundState: EvidenceState) => {
    return {
      ...outboundState,
      // Reset active states
      isRecording: false,
      recordingStartTime: null,
      uploadProgress: {},
      uploadErrors: [],
      // Evidence items are restored with available data URLs
    };
  },
  {
    whitelist: ['evidence']
  }
);

/**
 * Transform for customers slice - keep selection state but limit cache size
 */
export const customersTransform = createTransform(
  // Inbound: Limit cache size and remove large objects
  (inboundState: CustomersState) => {
    return {
      ...inboundState,
      // Limit cached customers to prevent storage bloat (keep last 50)
      customers: (inboundState.customers || []).slice(-50),
      // Keep essential selection state
      selectedCustomer: inboundState.selectedCustomer,
      recentSelections: (inboundState.recentSelections || []).slice(-10), // Keep last 10 recent selections
      searchHistory: (inboundState.searchHistory || []).slice(-20), // Keep last 20 searches
      // Reset loading and error states
      isLoading: false,
      error: null,
    };
  },
  // Outbound: Restore customer state
  (outboundState: CustomersState) => {
    return {
      ...outboundState,
      // Reset active states
      isLoading: false,
      error: null,
    };
  },
  {
    whitelist: ['customers']
  }
);

/**
 * Transform for navigation slice - preserve navigation context
 */
export const navigationTransform = createTransform(
  // Inbound: Keep navigation state for recovery
  (inboundState: any) => {
    return {
      ...inboundState,
      // Keep navigation context for state recovery
      currentScreen: inboundState.currentScreen,
      previousScreen: inboundState.previousScreen,
      navigationHistory: (inboundState.navigationHistory || []).slice(-20), // Keep last 20 navigation entries
      // Don't persist loading states
      isLoading: false,
    };
  },
  // Outbound: Restore navigation state
  (outboundState: any) => {
    return {
      ...outboundState,
      // Reset loading state on restore
      isLoading: false,
    };
  },
  {
    whitelist: ['navigation']
  }
);

/**
 * Transform for userState slice - selective persistence for session recovery
 */
export const userStateTransform = createTransform(
  // Inbound: Keep essential user state, limit interaction history
  (inboundState: any) => {
    return {
      ...inboundState,
      // Limit interaction history to prevent storage bloat
      interactions: (inboundState.interactions || []).slice(-50), // Keep last 50 interactions
      screenHistory: (inboundState.screenHistory || []).slice(-30), // Keep last 30 screens
      // Keep essential recovery data
      forms: inboundState.forms,
      drafts: inboundState.drafts,
      accessibility: inboundState.accessibility,
      settings: inboundState.settings,
      // Update session metadata
      lastActivity: Date.now(),
      // Keep modals state for recovery but reset active states
      modals: Object.fromEntries(
        Object.entries(inboundState.modals || {}).map(([key, modal]: [string, any]) => [
          key,
          { ...modal, isOpen: false } // Close all modals on persist
        ])
      ),
      // Keep media metadata with smart data URL handling
      mediaItems: Object.fromEntries(
        Object.entries(inboundState.mediaItems || {}).map(([key, item]: [string, any]) => {
          const MAX_DATA_URL_SIZE = 500000; // 500KB threshold
          const dataUrlSize = item.dataUrl ? item.dataUrl.length : 0;
          const shouldKeepDataUrl = dataUrlSize > 0 && dataUrlSize <= MAX_DATA_URL_SIZE;

          return [
            key,
            {
              ...item,
              // Keep data URL for small files
              dataUrl: shouldKeepDataUrl ? item.dataUrl : undefined,
              // Store metadata for restoration
              _persistenceMetadata: {
                hadDataUrl: !!item.dataUrl,
                originalSize: dataUrlSize,
                wasLarge: dataUrlSize > MAX_DATA_URL_SIZE,
                timestamp: Date.now(),
              },
            }
          ];
        })
      ),
    };
  },
  // Outbound: Restore user state with clean active states
  (outboundState: any) => {
    return {
      ...outboundState,
      // Generate new session ID on restore
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      // Update activity timestamp
      lastActivity: Date.now(),
      // Ensure all modals start closed
      modals: Object.fromEntries(
        Object.entries(outboundState.modals || {}).map(([key, modal]: [string, any]) => [
          key,
          { ...modal, isOpen: false }
        ])
      ),
    };
  },
  {
    whitelist: ['userState']
  }
);

// Export all transforms
export const persistTransforms = [
  visitsTransform,
  evidenceTransform,
  customersTransform,
  navigationTransform,
  userStateTransform,
];